import { findFirstNonEmptyString, notEmpty, theme, useResponsive } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentReadMoreContext } from "~/components/content/content-read-more";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { useTrackImpressionEventByIntersection } from "~/components/tracking/google-analytics/hooks";
import type { MoreOnThisWidgetEvents } from "~/components/tracking/google-analytics/types";
import { getAbsoluteUrl } from "~/lib/utils";
import type { articleMoreOnThisListStyleArticle$key } from "~/queries/__generated__/articleMoreOnThisListStyleArticle.graphql";

import {
  Container,
  Headline,
  Item,
  ItemContainer,
  StyledBaseImage,
  StyledEntityLink,
  StyledFigure,
  Title,
} from "./styles";

export type Props = {
  className?: string;
  reference: articleMoreOnThisListStyleArticle$key;
};

export const ArticleMoreOnThisList: FunctionComponent<Props> = ({
  className,
  reference: reference_,
}) => {
  const { styleMoreOnThisArticles } = useFragment(
    graphql`
      fragment articleMoreOnThisListStyleArticle on Article {
        styleMoreOnThisArticles: moreOnThisArticles {
          ...entityLink
          entityId
          headline
          socialHeadline
          urlAlias
          images {
            url
            style(filter: { style: "768x768" }) {
              url
            }
          }
        }
      }
    `,
    reference_,
  );

  const articles = styleMoreOnThisArticles?.filter(notEmpty).slice(0, 3);

  const impressionEvent = useCallback<() => MoreOnThisWidgetEvents>(
    () => ({
      action: "More on this Story/Impression",
      category: "More on this Story",
      label: "",
    }),
    [],
  );
  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection(
    impressionEvent,
    { intersectionThreshold: [0.1], shouldThrottle: true },
  );

  const handleRenderImage = (article: Article) => {
    const mainImage = first(article.images);
    const source = mainImage?.style?.url ?? mainImage?.url;
    return (
      source && (
        <StyledBaseImage
          $aspectRatio="auto"
          $objectFit="cover"
          alt={article.headline ?? undefined}
          src={source}
          disableLinkingData
        />
      )
    );
  };

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), true);
  const { isClicked } = useContentReadMoreContext();

  if (isMobile && !isClicked) return null;
  else if (!articles?.length) return null;

  return (
    <Container className={className} ref={captureTrackImpressionEventTargetElement}>
      <Title>
        / READ M<span>O</span>RE /
      </Title>
      <ItemContainer>
        {articles.map(article => (
          <Item key={article.entityId}>
            <StyledEntityLink
              reference={article}
              onClick={() => {
                sendGATracking({
                  action: "More on this Story/Click",
                  category: "More on this Story",
                  label: `${getAbsoluteUrl(article?.urlAlias)}`,
                });
              }}
            >
              <StyledFigure>{handleRenderImage(article)}</StyledFigure>
              <Headline>
                {findFirstNonEmptyString(article.socialHeadline, article.headline)}
              </Headline>
            </StyledEntityLink>
          </Item>
        ))}
      </ItemContainer>
    </Container>
  );

  type Article = Exclude<typeof articles, undefined>[number];
};

ArticleMoreOnThisList.displayName = "ArticleMoreOnThisList";
